'use client';

import { useEffect, useRef } from 'react';

/**
 * The one signal that means "something in this workspace just changed".
 *
 * Every screen here reads the same handful of records from a different angle:
 * deleting an order changes the orders table, the dashboard tiles, the to-do
 * list, the alert banner and the sidebar counts at once. Wiring each writer to
 * each reader by hand is what left the dashboard listing orders that were
 * already deleted until the user reloaded by hand, so writers announce the
 * change here instead and readers listen.
 */
export const DATA_CHANGED_EVENT = 'supplyautomate:data-changed';

/**
 * The predecessor of the event above, kept because Smart Upload and the pages
 * watching it already speak it. Anything listening for one now hears both.
 */
export const SMART_UPLOAD_EVENT = 'smart-upload-complete';

/**
 * Announce that server-side data changed.
 *
 * Safe to call from anywhere on the client, including several times in a row:
 * listeners coalesce a burst into a single refetch.
 */
export function notifyDataChanged() {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT));
}

/**
 * Re-read this screen's data whenever anything in the app writes.
 *
 * React Query views are already covered by the global listener in
 * `DataRefreshProvider`. This hook is for the screens that hold their rows in
 * their own `useState`, which leaves React Query nothing to invalidate.
 *
 * The callback is read through a ref so passing an inline arrow function does
 * not tear the subscription down and rebuild it on every render.
 */
export function useDataRefresh(onRefresh: () => void) {
  const handlerRef = useRef(onRefresh);

  useEffect(() => {
    handlerRef.current = onRefresh;
  });

  useEffect(() => {
    function handle() {
      handlerRef.current();
    }

    window.addEventListener(DATA_CHANGED_EVENT, handle);
    window.addEventListener(SMART_UPLOAD_EVENT, handle);

    return () => {
      window.removeEventListener(DATA_CHANGED_EVENT, handle);
      window.removeEventListener(SMART_UPLOAD_EVENT, handle);
    };
  }, []);
}
